import React, { ReactNode, useMemo } from "react"
import { Box, Flex, Text } from "@chakra-ui/react"
import Icon from "components/elements/Icon"
import GuestListIcon from "components/elements/GuestListIcon"
import { DioboxEvent } from "sharedTypes"
import _ from "lodash"
import pluralize from "pluralize"

type Props = {
  recipients: string[]
  guestCount: number
  event: DioboxEvent
}

type Row = {
  id: string
  label: string
  renderIcon: () => ReactNode
}

// Default segments are static. Hoisted so they're allocated once for the
// whole module, not per render. Their icons are also stable React elements.
const DEFAULT_SEGMENTS: Row[] = [
  { id: "group_all", label: "All Guests", renderIcon: () => <Icon icon="guests" size={6} color="blue.400" /> },
  { id: "group_confirmed", label: "Confirmed", renderIcon: () => <Icon icon="dot" color="blue.300" /> },
  { id: "group_unconfirmed", label: "Unconfirmed", renderIcon: () => <Icon icon="dot" color="gray.300" /> },
  { id: "group_waitlisted", label: "Waitlisted", renderIcon: () => <Icon icon="dot" color="yellow.300" /> },
  { id: "group_declined", label: "Declined", renderIcon: () => <Icon icon="dot" color="red.300" /> },
  { id: "group_checked_in", label: "Checked In", renderIcon: () => <Icon icon="dot" color="green.300" /> },
  { id: "group_unarrived", label: "Unarrived", renderIcon: () => <Icon icon="dot" color="orange.300" /> },
]

const SelectedRecipients = ({
  recipients,
  guestCount,
  event: {
    segments,
    eventGroups,
    tables: eventTables,
  },
}: Props) => {
  // `renderIcon` is a function (not a pre-built ReactNode) so we only pay
  // the JSX-allocation cost for rows that actually render after the
  // recipient filter — most rows in `allSegments` get filtered out and
  // their icons are never needed.
  const allSegments = useMemo<Row[]>(() => {
    const customSegments: Row[] = _.sortBy(segments, "icon").map(({ id, name, color }) => ({
      id: `segment_${id}`,
      label: name,
      renderIcon: () => <Icon icon="dot" color={`${color}.500`} />,
    }))
    const groups: Row[] = eventGroups.map(({
      id, title, icon, color,
    }) => ({
      id: `group_${id}`,
      label: title,
      renderIcon: () => <GuestListIcon icon={icon} color={color} />,
    }))
    const tables: Row[] = _.sortBy(eventTables, (t) => t.name.toLowerCase())
      .map(({ id, name }) => ({
        id: `table_${id}`,
        label: name,
        renderIcon: () => <Icon icon="tables-stroke" color="gray.600" />,
      }))

    return [...DEFAULT_SEGMENTS, ...customSegments, ...groups, ...tables]
  }, [segments, eventGroups, eventTables])

  const filteredSegments = allSegments.filter((segment) => recipients.includes(segment.id))

  return (
    <Box>
      <Flex alignItems="center" gap={2} mb={2}>
        {recipients.includes("group_all") ? <><Icon icon="guests" size={6} color="blue.400" /><Text>All Guests</Text></> : (
          <Box gap={3}>
            {filteredSegments.map(({ id, label, renderIcon }) => (
              <Flex key={id} alignItems="center" gap={2}>
                {renderIcon()}
                <Text>{label}</Text>
              </Flex>
            ))}
          </Box>
        )}
      </Flex>
      <Text fontSize="sm">{guestCount} {pluralize("recipient", guestCount)} with valid phone {pluralize("number", guestCount)}</Text>
    </Box>
  )
}

export default SelectedRecipients
